// On the off chance that an undefined value sneaks into localStorage,
// clear it out before doing any operations on the storage.
if (localStorage.getItem('survey') === 'undefined') localStorage.clear();

const survey = JSON.parse(localStorage.getItem('survey'));
const initialState =  {
    didFetchFail: false,
    didDirtyPostFail: false,
    didPostFail: false,
    error: '',
    isDataFetching: false,
    isDataFetched: survey && survey.hasOwnProperty('Evaluator'),
    isDataPosting: false,
    isDataPosted: false,
    isExpiredEval: false,
    isNoInternet: false,
    locationIndex: JSON.parse(localStorage.getItem('locationIndex')) || 0,
    surveyData: survey || {}
};

/**
 * Sets the data to the response of our request (made in the action),
 * and updates the boolean value representing its retrieval.
 */
const getData = (state) => {
    return {
        ...state,
        didFetchFail: false,
        isDataFetching: true,
        isDataFetched: false,
        isExpiredEval: false
    };
};

/**
 * Sets the state to show that an error occurred and sets the message
 * portion of the state to the error value.
 */
const getDataError = (state, action) => {
    return {
        ...state,
        didFetchFail: true,
        error: action.err,
        isDataFetching: false,
        isDataFetched: false
    };
};
const getExpiredEvaluationError = (state, action) => {
    return {
        ...state,
        isExpiredEval: true,
        didFetchFail: false,
        error: action.err,
        isDataFetching: false,
        isDataFetched: false
    };
};
/**
 * Updates the rating of the components for a given stay to default to 1. This
 * is needed because they all default to NULL initially, and should be set to 
 * 1 once the user visits the page. 
 */
const initComponentRatings = (state) => {
    const newLocations = state.surveyData.StaySubmissions.map((location, index) => {
        if (index === state.locationIndex) {
            // loop through the components of the current location, and
            // update the rating of the specified component.
            location.ComponentQuestion.Components = 
                location.ComponentQuestion.Components.map((component) => {
                    if (component.AnswerRating === null)
                        component.AnswerRating = 1;

                    return component;
                });
        }

        return location;
    });
    const newSurveyData = {
        ...state.surveyData,
        StaySubmissions: newLocations
    };

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Sets the state to show that an error occurred with the internet connection.
 */
const internetConnectionError = (state, action) => {
    return {
        ...state,
        isNoInternet: action.err
    };
};

/**
 * Sets the data to the response of our request (made in the action),
 * and updates the boolean value representing its retrieval.
 */
const getDataReceived = (state, action) => {
    updateLocalStorage(action.data);

    return {
        ...state,
        surveyData: action.data,
        didFetchFail: false,
        isDataFetching: false,
        isDataFetched: true
    };
};

/**
 * Sets the state to show that a post is occurring.
 */
const postData = (state) => {
    return {
        ...state,
        didDirtyPostFail: false,
        didPostFail: false,
        isDataPosted: false,
        isDataPosting: true
    };
};

/**
 * Sets the state to show that a post failed.
 */
const postDataError = (state, action) => {
    return {
        ...state,
        didPostFail: action.isSubmit,
        isDataPosted: false,
        isDataPosting: false
    };
};

/**
 * Sets the state to show that a post is done.
 */
const postDataReceived = (state, action) => {
    return {
        ...state,
        didPostFail: false,
        didDirtyPostFail: false,
        isDataPosted: action.isSubmit,
        isDataPosting: false
    };
};

/**
 * Sets the state to show that a 'dirty' post failed.
 */
const postDirtyDataError = (state, action) => {
    return {
        ...state,
        didDirtyPostFail: action.isSubmit,
        isDataPosted: false,
        isDataPosting: false
    };
};

/**
 * Updates the rating or comment portion of the specified component for the
 * current location based on the information in the action parameter.
 */
const setComponentAnswer = (state, action) => {
    const { comment, didNotRun, id, rating } = action.data;
    const newLocations = state.surveyData.StaySubmissions.map((location, index) => {
        if (index === state.locationIndex) {
            // loop through the components of the current location, and
            // update the rating of the specified component.
            location.ComponentQuestion.Components = 
                location.ComponentQuestion.Components.map((component) => {
                    if (component.SurveyComponentId === id) {
                        // Update the rating if we are given a value, or update
                        // the comment if we are given that instead.
                        if (typeof rating !== 'undefined') component.AnswerRating = rating;
                        if (typeof comment !== 'undefined') component.AnswerComment = comment;
                        if (typeof didNotRun !== 'undefined') component.DidNotRun = !component.DidNotRun;
                    }

                    return component;
                });

            location.Submission.isDirty = true;
        }

        return location;
    });
    const newSurveyData = {
        ...state.surveyData,
        StaySubmissions: newLocations
    };

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Updates the portion of the state that tracks the index of the current
 * location. Used to determine which location the user is currently in.
 */
const setLocationIndex = (state, action) => {
    // Update Local Storage with the current question index.
    localStorage.setItem('locationIndex', JSON.stringify(action.index));

    return {
        ...state,
        isDataPosted: false,
        locationIndex: action.index
    };
};

/**
 * Updates the isComplete, isInprogress, etc. portion for the current location.
 */
const setLocationStatus = (state, action) => {
    const newLocations = state.surveyData.StaySubmissions.map((question, index) => {
        if (index === state.locationIndex) 
            question.Submission[action.status] = true;

        return question;
    });
    const newSurveyData = {
        ...state.surveyData,
        StaySubmissions: newLocations
    };

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Updates the rating/answer portion of the supplied overall question.
 */
const setOverallAnswer = (state, action) => {
    let tdInstances = 0;
    const { comment, isNotApplicable, tdIndex, rating, type } = action.data;
    const newOveralls = state.surveyData.OverallSubmission.OverallQuestions.map((question) => {
        let answer = question.Answer;
        
        if (question.Type.toLowerCase().includes('tourdirector')) {
            tdInstances++;
            // Make sure we update the right TD question.
            if (tdInstances === tdIndex) {
                // Update the rating or the comment if we get a value for it.
                if (typeof rating !== 'undefined') question.Answer.AnswerRating = rating;
                if (typeof comment !== 'undefined') question.Answer.AnswerComment = comment;
                if (isNotApplicable) {
                    question.Answer.IsNotApplicable = !question.Answer.IsNotApplicable;
                    question.Answer.AnswerRating = null;
                } 
            }
        } else if (question.Type === type) {
            // Update the rating or the comment if we get a value for it.
            if (typeof rating !== 'undefined') question.Answer.AnswerRating = rating;
            if (typeof comment !== 'undefined') question.Answer.AnswerComment = comment;
        }

        return question;
    });
    const newSurveyData = {
        ...state.surveyData,
        OverallSubmission: {
            ...state.surveyData.OverallSubmission,
            OverallQuestions: newOveralls,
            Submission: {
                ...state.surveyData.OverallSubmission.Submission,
                isDirty: true
            }
        }
    };

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Updates the isComplete, isInprogress, etc. portion for the overall questions.
 */
const setOverallStatus = (state, action) => {
    const newSurveyData = JSON.parse(JSON.stringify(state.surveyData));
    
    // Not the 'redux way', but much more effective IMO
    newSurveyData.OverallSubmission.Submission[action.status] = true;

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Goes through all of the stays to find the one where the edit was made,
 * and updates the comment value for the Pulse check portion.
 */
const setPulseCheckComment = (state, action) => {
    const newLocations = state.surveyData.StaySubmissions.map((location, index) => {
        if (index === state.locationIndex) {
            location.PulseCheck.AnswerComment = action.comment;
            location.Submission.isDirty = true;
        }

        return location;
    });
    const newSurveyData = {
        ...state.surveyData,
        StaySubmissions: newLocations
    };

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Goes through all of the stays to find the one where the edit was made,
 * and updates the rating value for the Pulse check portion.
 */
const setStarRatingValue = (state, action) => {
    const newLocations = state.surveyData.StaySubmissions.map((location, index) => {
        if (index === state.locationIndex) {
            location.PulseCheck.AnswerRating = action.rating;
            location.Submission.isDirty = true;
        }

        return location;
    });
    const newSurveyData = {
        ...state.surveyData,
        StaySubmissions: newLocations
    }; 

    updateLocalStorage(newSurveyData);

    return {
        ...state,
        surveyData: newSurveyData
    };
};

/**
 * Helper function to update the survey in local storage.
 */
const updateLocalStorage = (localStorageValue) => {
    localStorage.setItem('survey', JSON.stringify(localStorageValue));
}


/**
 * Our reducer dispatcher. Calls the reducer specified by the supplied
 * action type, and passes it the supplied state. 
 */
const surveyReducer = (state = initialState, action) => {
    switch (action.type) {
        case 'GET_DATA': return getData(state, action);
        case 'GET_DATA_ERROR': return getDataError(state, action);
        case 'GET_EXPIRED_EVALUATION_ERROR': return getExpiredEvaluationError(state, action);
        case 'GET_DATA_RECEIVED': return getDataReceived(state, action);
        case 'GET_ENCRYPTED_DATA_RECEIVED': return getDataReceived(state, action);
        case 'INIT_COMPONENT_RATINGS': return initComponentRatings(state);
        case 'INTERNET_CONNECTION_ERROR': return internetConnectionError(state, action);
        case 'POST_DATA': return postData(state, action);
        case 'POST_DATA_ERROR': return postDataError(state, action);
        case 'POST_DIRTY_DATA_ERROR': return postDirtyDataError(state, action);
        case 'POST_DATA_RECEIVED': return postDataReceived(state, action);
        case 'SET_PULSE_CHECK_COMMENT': return setPulseCheckComment(state, action);
        case 'SET_COMPONENT_ANSWER': return setComponentAnswer(state, action);
        case 'SET_LOCATION_INDEX': return setLocationIndex(state, action);
        case 'SET_LOCATION_STATUS': return setLocationStatus(state, action);
        case 'SET_OVERALL_ANSWER': return setOverallAnswer(state, action);
        case 'SET_OVERALL_STATUS': return setOverallStatus(state, action);
        case 'SET_STAR_RATING_VALUE': return setStarRatingValue(state, action);
        default: return state;
    }
};

export default surveyReducer;



// WEBPACK FOOTER //
// ./application/reducers/surveyReducer.js